class OpenAIService {
  async sendMessage(message, context, userEmail = null, userName = null, conversationHistory = null, onChunk = null) {
    try {
      const apiKey = import.meta.env.VITE_OPENAI_API_KEY;
      const useDirectApi = !!(apiKey && apiKey.startsWith('sk-'));
      const useStreaming = typeof onChunk === 'function';

      const API_ENDPOINT = useDirectApi
        ? 'https://api.openai.com/v1/chat/completions'
        : '/.netlify/functions/chat';

      const formattedHistory = conversationHistory
        ? conversationHistory.map(msg => ({
            role: msg.sender === 'user' ? 'user' : 'assistant',
            content: msg.text,
          }))
        : [];

      const headers = { 'Content-Type': 'application/json' };
      if (useDirectApi) headers['Authorization'] = `Bearer ${apiKey}`;

      const messages = [
        { role: 'system', content: context },
        ...formattedHistory,
      ];

      const body = useDirectApi
        ? { model: 'gpt-4o-mini', messages, max_tokens: 400, temperature: 0.15, stream: useStreaming }
        : { message, context, conversationHistory: formattedHistory, stream: useStreaming };

      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          const err = await response.json();
          throw new Error(err.error?.message || 'API Error');
        }
        return "I'm having trouble connecting. Please try again or call us at (770) 643-8812.";
      }

      // --- STREAMING MODE ---
      if (useStreaming && response.body) {
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let fullText = '';
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop(); // keep incomplete line

          for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed || trimmed === 'data: [DONE]') continue;
            if (trimmed.startsWith('data: ')) {
              try {
                const json = JSON.parse(trimmed.slice(6));
                const chunk = json.choices?.[0]?.delta?.content || '';
                if (chunk) {
                  fullText += chunk;
                  onChunk(chunk, fullText);
                }
              } catch {
                // skip malformed chunk
              }
            }
          }
        }
        return fullText;
      }

      // --- NON-STREAMING MODE ---
      const data = await response.json();
      if (data.choices?.[0]) return data.choices[0].message.content;
      throw new Error('Invalid response format');

    } catch (error) {
      console.error('Chat Service Error:', error);
      return "I'm having trouble connecting. Please try again or call us at (770) 643-8812.";
    }
  }
}

export const openaiService = new OpenAIService();
